import { UnitCategory } from '../types/units';

interface Formula {
  name: string;
  expression: string;
  description?: string;
}

export const commonFormulas: Partial<Record<UnitCategory, Formula[]>> = {
  length: [
    {
      name: 'Rectangle Perimeter',
      expression: '2 * (5m + 3m)',
      description: 'Calculates the perimeter of a rectangle'
    },
    {
      name: 'Circle Circumference',
      expression: '2 * 3.14159 * 5m',
      description: 'Calculates the circumference of a circle'
    },
    {
      name: 'Triangle Perimeter',
      expression: '3m + 4m + 5m',
      description: 'Calculates the perimeter of a triangle'
    },
    {
      name: 'Meter to Feet',
      expression: '1m * 3.28084',
      description: 'Converts meters to feet'
    },
    {
      name: 'Square Diagonal',
      expression: 'sqrt(5m * 5m + 5m * 5m)',
      description: 'Calculates the diagonal of a square'
    },
    {
      name: 'Yard to Meter',
      expression: '100yd * 0.9144',
      description: 'Converts yards to meters'
    }
  ],
  area: [
    {
      name: 'Rectangle Area',
      expression: '5m * 3m',
      description: 'Calculates the area of a rectangle'
    },
    {
      name: 'Circle Area',
      expression: '3.14159 * (5m * 5m)',
      description: 'Calculates the area of a circle'
    }
  ],
  volume: [
    {
      name: 'Cube Volume',
      expression: '5m * 5m * 5m',
      description: 'Calculates the volume of a cube'
    }
  ],
  weight: [
    {
      name: 'Weight to Mass',
      expression: '1kg * 9.81',
      description: 'Converts mass to weight force'
    },
    {
      name: 'BMI Calculation',
      expression: '75kg / (1.8m * 1.8m)',
      description: 'Calculates Body Mass Index'
    },
    {
      name: 'Weight Distribution',
      expression: '100kg / 4',
      description: 'Calculates weight per point for even distribution'
    },
    {
      name: 'Cargo Load',
      expression: '1000kg + 500kg + 250kg',
      description: 'Sums multiple weights for total load'
    }
  ],
  speed: [
    {
      name: 'Average Speed',
      expression: '60km / 2h',
      description: 'Calculates average speed from distance and time'
    }
  ],
  temperature: [
    {
      name: 'Average Temperature',
      expression: '(25celsius + 30celsius) / 2',
      description: 'Calculates the average of two temperatures'
    },
    {
      name: 'Temperature Difference',
      expression: '35celsius - 20celsius',
      description: 'Calculates temperature change'
    },
    {
      name: 'Cooking Temperature',
      expression: '180celsius + 20celsius',
      description: 'Adjusts cooking temperature with offset'
    }
  ],
  pressure: [
    {
      name: 'Pressure at Depth',
      expression: '1atm + (10m * 0.1bar)',
      description: 'Calculates pressure at a given depth'
    },
    {
      name: 'Tire Pressure',
      expression: '32psi + 2psi',
      description: 'Adjusts tire pressure with safety margin'
    },
    {
      name: 'Pressure Conversion',
      expression: '1bar * 14.5038',
      description: 'Converts bar to PSI'
    }
  ],
  energy: [
    {
      name: 'Kinetic Energy',
      expression: '0.5 * 10kg * (5mps * 5mps)',
      description: 'Calculates kinetic energy'
    },
    {
      name: 'Potential Energy',
      expression: '1kg * 9.81 * 10m',
      description: 'Calculates gravitational potential energy'
    },
    {
      name: 'Energy Conversion',
      expression: '1kwh * 3600000',
      description: 'Converts kilowatt-hours to joules'
    }
  ],
  power: [
    {
      name: 'Electrical Power',
      expression: '220v * 5a',
      description: 'Calculates electrical power'
    },
    {
      name: 'Hours to Minutes',
      expression: '2h * 60',
      description: 'Converts hours to minutes'
    },
    {
      name: 'Days to Hours',
      expression: '7d * 24',
      description: 'Converts days to hours'
    },
    {
      name: 'Weeks to Days',
      expression: '2w * 7',
      description: 'Converts weeks to days'
    },
    {
      name: 'Minutes to Seconds',
      expression: '30min * 60',
      description: 'Converts minutes to seconds'
    }
  ],
  data: [
    {
      name: 'Download Time',
      expression: '5gb / (50mb / 1s)',
      description: 'Calculates download time'
    },
    {
      name: 'Storage Conversion',
      expression: '1024mb * 1024',
      description: 'Converts megabytes to kilobytes'
    },
    {
      name: 'Gigabyte to Megabyte',
      expression: '2.5gb * 1024',
      description: 'Converts gigabytes to megabytes'
    },
    {
      name: 'Terabyte to Gigabyte',
      expression: '1.5tb * 1024',
      description: 'Converts terabytes to gigabytes'
    }
  ],
  frequency: [
    {
      name: 'Wave Period',
      expression: '1 / 440hz',
      description: 'Calculates wave period from frequency'
    },
    {
      name: 'Frequency Doubling',
      expression: '440hz * 2',
      description: 'Calculates octave up in music'
    },
    {
      name: 'Sample Rate',
      expression: '44100hz / 1000',
      description: 'Converts sample rate to milliseconds'
    }
  ],
  textile: [
    {
      name: 'Yarn Count Conversion',
      expression: '1denier * 9tex',
      description: 'Converts between yarn count systems'
    }
  ],
  astronomy: [
    {
      name: 'Light Travel Time',
      expression: '1ly / 299792458mps',
      description: 'Calculates time for light to travel a distance'
    }
  ],
  surveying: [
    {
      name: 'Area in Acres',
      expression: '1chain * 10chain',
      description: 'Calculates area in acres from chains'
    }
  ],
  viscosity: [
    {
      name: 'Kinematic Viscosity',
      expression: '1poise / 1gcm3',
      description: 'Calculates kinematic viscosity from dynamic viscosity'
    }
  ],
  magnetic: [
    {
      name: 'Magnetic Flux',
      expression: '1tesla * 1m * 1m',
      description: 'Calculates magnetic flux'
    }
  ],
  lumber: [
    {
      name: 'Board Feet',
      expression: '1fbm * 12',
      description: 'Converts board feet to cubic feet'
    }
  ],
  gardening: [
    {
      name: 'Plant Spacing',
      expression: '43560 / (3spacing_ft * 3spacing_ft)',
      description: 'Calculates plants per acre based on spacing'
    }
  ],
  brewing: [
    {
      name: 'ABV from Gravity',
      expression: '(1.05sg - 1sg) * 131.25',
      description: 'Calculates alcohol by volume from specific gravity'
    }
  ],
  shoe: [
    {
      name: 'US to EU Size',
      expression: '33 + (us_men * 1)',
      description: 'Converts US mens size to EU size'
    }
  ],
  maritime: [
    {
      name: 'Distance in Nautical Miles',
      expression: '60nmile * cos(45deg)',
      description: 'Calculates distance considering latitude'
    }
  ],
  music: [
    {
      name: 'Tempo to Period',
      expression: '60 / 120bpm',
      description: 'Converts BPM to seconds per beat'
    }
  ],
  sports: [
    {
      name: 'Running Pace',
      expression: '10km / (45min)',
      description: 'Calculates running pace per kilometer'
    }
  ],
  art: [
    {
      name: 'Golden Ratio',
      expression: '1 * 1.618034',
      description: 'Calculates golden ratio proportion'
    }
  ],
  gaming: [
    {
      name: 'DPS Calculation',
      expression: '100dps * (1 + 0.5)',
      description: 'Calculates damage per second with multiplier'
    }
  ],
  finance: [
    {
      name: 'Compound Interest',
      expression: '1000 * (1 + 0.05) ^ 5',
      description: 'Calculates compound interest over time'
    }
  ],
  geology: [
    {
      name: 'Earthquake Energy',
      expression: '10 ^ (1.5 * richter + 4.8)',
      description: 'Calculates energy release from Richter scale'
    }
  ],
  chemistry: [
    {
      name: 'Molarity',
      expression: '(25g / 58.44) / 0.5l',
      description: 'Calculates molarity from mass and volume'
    }
  ],
  weather: [
    {
      name: 'Wind Chill',
      expression: '13.12 + 0.6215 * 0celsius - 11.37 * (5kph ^ 0.16)',
      description: 'Calculates wind chill temperature'
    }
  ],
  construction: [
    {
      name: 'Concrete Volume',
      expression: '10m * 10m * 0.1m',
      description: 'Calculates concrete volume needed'
    }
  ],
  printing: [
    {
      name: 'Paper Coverage',
      expression: '(8.5in_type * 11in_type) * 0.8',
      description: 'Calculates printable area with margins'
    }
  ],
  flow: [
    {
      name: 'Pipe Flow Rate',
      expression: '10lps * 60',
      description: 'Converts liters per second to liters per minute'
    },
    {
      name: 'Flow Conversion',
      expression: '100cfm * 1.699',
      description: 'Converts cubic feet per minute to cubic meters per hour'
    }
  ],
  sound: [
    {
      name: 'Sound Level Addition',
      expression: '10 * log10(10^(80db/10) + 10^(70db/10))',
      description: 'Combines two sound levels'
    },
    {
      name: 'Distance Attenuation',
      expression: '90db - 20 * log10(10)',
      description: 'Calculates sound level at distance'
    }
  ],
  density: [
    {
      name: 'Mass from Volume',
      expression: '1000kgm3 * 2',
      description: 'Calculates mass from volume and density'
    },
    {
      name: 'Specific Gravity',
      expression: '0.9gcm3 / 1gcm3',
      description: 'Calculates specific gravity relative to water'
    }
  ]
};